import styled from "styled-components";
import { theme } from "../../../theme";
import { MailIconDainty } from "../../atoms/icons/MailIcon";
import BodySmall from "../../atoms/typography/BodySmall";
import Tooltip from "../Tooltip";
import { useState } from "react";
import Modal from "../Modal";
import type { EmailDetailsModel } from "../../../models/EmailDetailsModel";
import { WarningIcon } from "../../atoms/icons/WarningIcon";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import EmailDisplayModalContent from "../modals/EmailDisplayModalContent";
import { useTranslation } from "react-i18next";
import Small from "../../atoms/typography/Small";
import type { CommentModel } from "../../../models/CommentModel";
import dayjs from "dayjs";
import Body from "../../atoms/typography/Body";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { InfoIconSmall } from "../../atoms/icons/InfoIcons";

interface Props {
  type: "mail" | "blacklist" | "invalidFiles" | "rejectionComment";
  emailContent?: EmailDetailsModel;
  rejection?: CommentModel;
}

const BadgeContainer = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 2px;
  width: fit-content;
`;

const EmailBodyContainer = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  img {
    max-width: 100%;
    height: auto;
  }

  table {
    max-width: 100%;
    width: auto;
    border-collapse: collapse;
    table-layout: auto;
  }
`;

export const EmailBody: React.FC<{ rawHtml: string }> = ({ rawHtml }) => {
  return <EmailBodyContainer dangerouslySetInnerHTML={{ __html: rawHtml }} />;
};

const UploadUnitNotificationsComponents: React.FC<Props> = ({
  type,
  emailContent,
  rejection,
}) => {
  const { t } = useTranslation();
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  return (
    <div>
      {type === "mail" && (
        <Tooltip
          width="400px"
          content={
            <BodySmall color={theme.colors.white}>
              {emailContent?.previewBody}
            </BodySmall>
          }
        >
          <BadgeContainer
            color={theme.colors.grey05}
            style={{ cursor: "pointer" }}
            onClick={() => setEmailModalOpen(true)}
          >
            <MailIconDainty color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("common.mail").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}

      {type === "blacklist" && (
        <Tooltip
          content={
            <BodySmall color={theme.colors.white}>
              {t("documentHandler.recipientOrIssuerIsOnBlacklist")}
            </BodySmall>
          }
        >
          <BadgeContainer color={theme.colors.error}>
            <WarningIcon color={theme.colors.white} />
            <Small fontweight={700} color={theme.colors.white}>
              {t("menu.blacklist").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}

      {type === "invalidFiles" && (
        <Tooltip
          content={
            <BodySmall color={theme.colors.white}>
              {t(
                "documentHandler.theMailIncludesAttachmentsThatCouldNotBeInterpreted",
              )}
            </BodySmall>
          }
        >
          <BadgeContainer color={theme.colors.warning20}>
            <CrossIcon color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("documentHandler.invalidAttachment").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}

      {type === "rejectionComment" && rejection && (
        <Tooltip
          content={
            <>
              <div>
                <BodySmall color={theme.colors.white}>
                  {dayjs(rejection.createdAt).format("YYYY-MM-DD")} -{" "}
                  {rejection.createdByUser?.firstName}{" "}
                  {rejection.createdByUser?.lastName}
                </BodySmall>
                <Body color={theme.colors.white}> {rejection.content}</Body>
                <HorizontalSpacer spacing={1} />
              </div>
            </>
          }
          width="500px"
        >
          <BadgeContainer color={theme.colors.info05}>
            <InfoIconSmall color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("common.info").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}

      {emailModalOpen && emailContent && (
        <Modal onClose={() => setEmailModalOpen(false)} closeIcon>
          <EmailDisplayModalContent emailContent={emailContent} />
        </Modal>
      )}
    </div>
  );
};

export default UploadUnitNotificationsComponents;
